export const formatDateTime = (
  value?: string | number | Date | null,
  locale: string = "en-GB",
  fallback: string = "N/A"
) => {
  if (value === null || value === undefined || value === "") {
    return fallback;
  }

  const date = value instanceof Date ? value : new Date(value);

  if (Number.isNaN(date.getTime())) {
    return fallback;
  }

  const formatter = new Intl.DateTimeFormat(locale, {
    day: "2-digit",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });

  const formatted = formatter.format(date);

  const normalized = formatted.includes(" at ")
    ? formatted.replace(" at ", ", ")
    : formatted;

  return normalized.replace(/\b(am|pm)\b/gi, (match) => match.toUpperCase());
};

interface FormatOptions {
  hideDecimal?: boolean;
  hideSymbol?: boolean;
  convertToNumber?: boolean;
  symbol?: string;
  removeLeadingZero?: boolean;
  numberAbbreviate?: boolean;
}

const formatNumberWithSymbols = (number: number) => {
  const formats = [
    { value: 1, symbol: "" },
    { value: 1e3, symbol: "k" },
    { value: 1e6, symbol: "M" },
    { value: 1e9, symbol: "B" },
    { value: 1e12, symbol: "T" },
    { value: 1e15, symbol: "Q" },
    { value: 1e18, symbol: "QT" },
    { value: 1e21, symbol: "QQ" },
  ];
  const format = formats
    .slice()
    .reverse()
    .find((fmt) => number >= fmt.value);
  if (format) {
    return (
      (number / format.value).toFixed(2).replace(/\.00$/, "") + format.symbol
    );
  }
  return number.toString();
};

const getCurrencySymbol = (param: string) => {
  const nairaSymbol = "\u{020A6}";
  const upperParam = String(param).toUpperCase();
  return upperParam === "NGN" || upperParam === "NAIRA"
    ? nairaSymbol
    : upperParam === "USD" || upperParam === "DOLLAR"
      ? "$"
      : upperParam === "EUR" || upperParam === "EURO"
        ? "€"
        : upperParam === "ZAR"
          ? "R"
          : upperParam === "KES"
            ? "KSh"
            : upperParam === "CAD"
              ? "$"
              : upperParam === "GHS"
                ? "₵"
                : upperParam === "GBP" ||
                    upperParam === "POUNDS" ||
                    upperParam === "POUND"
                  ? "£"
                  : param;
};

export const glooproNumberFormat = (
  number: number | string | null | undefined,
  options?: FormatOptions
): string => {
  const symbol = getCurrencySymbol(options?.symbol || "NGN");
  const hideDecimal = options?.hideDecimal || false;
  const hideSymbol = options?.hideSymbol || false;
  const convertToNumber = options?.convertToNumber || false;
  const removeLeadingZero = options?.removeLeadingZero || false;
  const numberAbbreviate = options?.numberAbbreviate || false;
  if (number !== null && number !== undefined) {
    // Remove symbols and non-numeric characters
    const numericValue: string = number.toString().replace(/[^0-9.]/g, "");
    // Separate the integer and decimal parts
    let [integerPart, decimalPart] = numericValue.split(".");
    if (removeLeadingZero) {
      // Remove leading zeros if more than one leading zero
      if (/^0{2,}/.test(integerPart)) {
        integerPart = integerPart.replace(/^0+/, "");
      }
    }
    if (convertToNumber) {
      // Return number without thousand formatting and without symbol
      const val = decimalPart ? `${integerPart}.${decimalPart}` : integerPart;
      return val;
    }
    const formattedValue = Number(
      integerPart + (decimalPart ? "." + decimalPart : "")
    );
    if (numberAbbreviate) {
      const formatted = formatNumberWithSymbols(formattedValue);
      return hideSymbol ? formatted : `${symbol}${formatted}`;
    }
    // Format the integer part with thousand separator
    integerPart = Number(integerPart).toLocaleString();
    // Handle the decimal part
    if (!hideDecimal) {
      if (decimalPart) {
        // Convert the decimal part back to a number and round to two significant figures
        decimalPart = Number("0." + decimalPart)
          .toPrecision(2)
          .split(".")[1];
        // Remove trailing zeros from the decimal part
        decimalPart = decimalPart?.replace(/0+$/, "") || "";
      }
      const val = decimalPart
        ? `${integerPart}${decimalPart ? "." : ""}${decimalPart}`
        : `${integerPart}.00`;
      return hideSymbol ? val : `${symbol}${val}`;
    } else {
      return hideSymbol ? integerPart : `${symbol}${integerPart}`;
    }
  } else {
    return hideSymbol ? `0.00` : `${symbol}0.00`;
  }
};

/**
 * Generates a URL query string from a given object of parameters.
 *
 * Filters out parameters with `undefined` or `null` or empty string values. If a parameter value is an array,
 * it generates multiple key-value pairs for each element in the array.
 *
 * @param params - An object containing key-value pairs to be converted into a query string. Values can be strings, numbers, or arrays of these types.
 * @returns The generated query string, starting with '?' if there are parameters, or an empty string if no valid parameters are provided.
 *
 * @example
 * generateQueryString({ foo: "bar", baz: [1, 2], empty: undefined });
 * // Returns: "?foo=bar&baz=1&baz=2"
 */
export function generateQueryString(params: Record<string, any>): string {
  const query = Object.entries(params)
    .filter(
      ([_, value]) => value !== undefined && value !== null && value !== ""
    ) // filter out undefined/null
    .map(([key, value]) =>
      Array.isArray(value)
        ? value
            .map(
              (val) => `${encodeURIComponent(key)}=${encodeURIComponent(val)}`
            )
            .join("&")
        : `${encodeURIComponent(key)}=${encodeURIComponent(value)}`
    )
    .join("&");

  return query ? `?${query}` : "";
}

export const removeKeyFromObject = (obj: any, key: string) => {
  const { [key]: _, ...rest } = obj;
  return rest;
};
